import { registerPlugin } from "@capacitor/core"
import { getFavoritePaths } from "./navFavorites"

export interface AppShortcutItem {
  id: string
  label: string
  longLabel: string
  route: string
  icon: string
}

export interface NativeShortcutsPlugin {
  getInitialRoute(): Promise<{ route: string }>
  setDynamicShortcuts(options: { shortcuts: AppShortcutItem[] }): Promise<{ count: number; supported: boolean }>
}

export const NativeShortcuts = registerPlugin<NativeShortcutsPlugin>("NativeShortcuts", {
  web: () => ({
    async getInitialRoute() {
      return { route: "" }
    },
    async setDynamicShortcuts() {
      return { count: 0, supported: false }
    },
  }),
})

export const CUSTOM_SHORTCUTS_KEY = "servernox:custom-launcher-shortcuts"

export interface ShortcutCandidate {
  route: string
  label: string
  desc: string
  icon: string
}

export const SHORTCUT_CANDIDATE_OPTIONS: ShortcutCandidate[] = [
  { route: "/terminal", label: "Terminal", desc: "Web SSH Terminali", icon: "ic_shortcut_terminal" },
  { route: "/cleaner", label: "Temizleyici", desc: "Disk & Kaynak Temizleyici", icon: "ic_shortcut_cleaner" },
  { route: "/ai-coding", label: "AI Coding", desc: "Yapay Zeka Kod Asistanı", icon: "ic_shortcut_ai" },
  { route: "/docker", label: "Docker", desc: "Konteyner ve Stack Yönetimi", icon: "ic_shortcut_docker" },
  { route: "/websites", label: "Websiteler", desc: "Site ve Alan Adı Yönetimi", icon: "ic_shortcut_websites" },
  { route: "/files", label: "Dosyalar", desc: "Gelişmiş Dosya Yöneticisi", icon: "ic_shortcut_files" },
  { route: "/servers", label: "Sunucular", desc: "Sunucu ve Düğüm Listesi", icon: "ic_shortcut_servers" },
  { route: "/dashboard", label: "Genel Bakış", desc: "Sistem ve Donanım Paneli", icon: "ic_shortcut_dashboard" },
  { route: "/settings", label: "Ayarlar", desc: "Panel & Uygulama Ayarları", icon: "ic_shortcut_settings" },
  { route: "/mariadb", label: "MariaDB", desc: "Veritabanı Yönetimi", icon: "ic_shortcut_default" },
  { route: "/postgresql", label: "PostgreSQL", desc: "PostgreSQL Veritabanı", icon: "ic_shortcut_default" },
  { route: "/redis", label: "Redis", desc: "Bellek ve Önbellek Yönetimi", icon: "ic_shortcut_default" },
  { route: "/nginx", label: "Nginx", desc: "Web Sunucu Yapılandırması", icon: "ic_shortcut_default" },
  { route: "/logs", label: "Loglar", desc: "Canlı Sistem Logları", icon: "ic_shortcut_default" },
]

const ROUTE_SHORTCUT_METADATA: Record<string, { label: string; longLabel: string; icon: string }> = {
  "/": { label: "Genel Bakış", longLabel: "Sunucu Paneli", icon: "ic_shortcut_dashboard" },
  "/dashboard": { label: "Genel Bakış", longLabel: "Sunucu Paneli", icon: "ic_shortcut_dashboard" },
  "/terminal": { label: "Terminal", longLabel: "Web Terminal (SSH)", icon: "ic_shortcut_terminal" },
  "/files": { label: "Dosyalar", longLabel: "Dosya Yöneticisi", icon: "ic_shortcut_files" },
  "/ai-coding": { label: "AI Coding", longLabel: "AI Kodlama ve Asistan", icon: "ic_shortcut_ai" },
  "/docker": { label: "Docker", longLabel: "Docker Konteynerleri", icon: "ic_shortcut_docker" },
  "/websites": { label: "Websiteler", longLabel: "Web Site Yönetimi", icon: "ic_shortcut_websites" },
  "/servers": { label: "Sunucular", longLabel: "Sunucu Listesi", icon: "ic_shortcut_servers" },
  "/dashboard?cleaner=disk": { label: "Temizleyici", longLabel: "Disk Temizleyici", icon: "ic_shortcut_cleaner" },
  "/cleaner": { label: "Temizleyici", longLabel: "Disk Temizleyici", icon: "ic_shortcut_cleaner" },
  "/settings": { label: "Ayarlar", longLabel: "Panel Ayarları", icon: "ic_shortcut_settings" },
  "/supervisor": { label: "Supervisor", longLabel: "Servis Yöneticisi", icon: "ic_shortcut_default" },
  "/mariadb": { label: "MariaDB", longLabel: "MariaDB Veritabanı", icon: "ic_shortcut_default" },
  "/postgresql": { label: "PostgreSQL", longLabel: "PostgreSQL Veritabanı", icon: "ic_shortcut_default" },
  "/redis": { label: "Redis", longLabel: "Redis Bellek Öncesi", icon: "ic_shortcut_default" },
  "/nginx": { label: "Nginx", longLabel: "Nginx Web Sunucusu", icon: "ic_shortcut_default" },
  "/logs": { label: "Loglar", longLabel: "Sistem Logları", icon: "ic_shortcut_default" },
}

export function getCustomShortcutPaths(): string[] | null {
  try {
    const raw = localStorage.getItem(CUSTOM_SHORTCUTS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed.length > 0) return parsed
  } catch {}
  return null
}

export function setCustomShortcutPaths(paths: string[] | null): void {
  try {
    if (paths && paths.length > 0) {
      localStorage.setItem(CUSTOM_SHORTCUTS_KEY, JSON.stringify(paths))
    } else {
      localStorage.removeItem(CUSTOM_SHORTCUTS_KEY)
    }
  } catch {}
}

export function resolveShortcutForPath(path: string, index = 0): AppShortcutItem {
  const cleanPath = path.startsWith("/") ? path : `/${path}`
  const meta = ROUTE_SHORTCUT_METADATA[cleanPath] || {
    label: cleanPath.replace(/^\//, "").replace(/[-_]/g, " ") || "Kısayol",
    longLabel: `ServerNox ${cleanPath}`,
    icon: "ic_shortcut_default",
  }
  const slug = cleanPath.replace(/[^a-zA-Z0-9]/g, "_").replace(/^_+|_+$/g, "") || String(index)
  const id = `dyn_slot_${index}_${slug}`
  return {
    id,
    label: meta.label,
    longLabel: meta.longLabel,
    route: cleanPath,
    icon: meta.icon,
  }
}

export function getActiveShortcutItems(customPaths?: string[]): AppShortcutItem[] {
  const custom = customPaths && customPaths.length > 0 ? customPaths : getCustomShortcutPaths()
  const paths = custom || getFavoritePaths()
  const fallback = ["/ai-coding", "/websites", "/docker", "/terminal"]
  const merged: string[] = []
  for (const p of paths) {
    const clean = p.startsWith("/") ? p : `/${p}`
    if (!merged.includes(clean)) merged.push(clean)
  }
  for (const f of fallback) {
    if (merged.length >= 4) break
    if (!merged.includes(f)) merged.push(f)
  }
  return merged.slice(0, 4).map((p, idx) => resolveShortcutForPath(p, idx))
}

export async function getNativeInitialRoute(): Promise<string | null> {
  try {
    const res = await NativeShortcuts.getInitialRoute()
    return res?.route || null
  } catch {
    return null
  }
}

export async function syncNativeShortcuts(customPaths?: string[]): Promise<boolean> {
  const shortcuts = getActiveShortcutItems(customPaths)
  try {
    const res = await NativeShortcuts.setDynamicShortcuts({ shortcuts })
    return !!res?.supported
  } catch (err) {
    console.warn("Failed to sync native shortcuts:", err)
    return false
  }
}
